import React, { useRef, useEffect, useCallback } from "react";
import { GdsDateInput } from "@gds/web-components/react";

type DatePickerProps = {
  value: string;
  minDate?: string;
  maxDate?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputId: string;
  name: string;
  placeholder?: string;
  disabled?: boolean;
  dateFormat?: "MM dd yyyy" | "MM yyyy" | "MM yy" | "none";
  labelText?: string;
  highlight?: boolean;
};

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  minDate,
  maxDate,
  onChange,
  inputId,
  name,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  dateFormat = "MM dd yyyy",
  labelText,
  highlight = false,
}) => {
  const ref = useRef<any>(null);
  const hasSetInitialValue = useRef(false);

  useEffect(() => {
    if (
      ref.current &&
      typeof ref.current.updateAirDatePickerOptions === "function"
    ) {
      ref.current.updateAirDatePickerOptions({
        ...(minDate ? { minDate } : {}),
        ...(maxDate ? { maxDate } : {}),
      });
    }
  }, [minDate, maxDate]);

  useEffect(() => {
    if (ref.current && value && !hasSetInitialValue.current) {
      const timer = setTimeout(() => {
        try {
          if (ref.current.setDateInputValue && typeof ref.current.setDateInputValue === 'function') {
            ref.current.setDateInputValue(value);
            hasSetInitialValue.current = true;
          }
        } catch (error) {
          console.log('Error setting date value:', error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [value]);
  const handleDateChange = useCallback(
    (e: CustomEvent) => {
      let val = e.detail.value;
      if (/^\d{2} \d{2} \d{4}$/.test(val)) {
        const [mm, dd, yyyy] = val.split(" ");
        val = `${mm}/${dd}/${yyyy}`;
      }

      const syntheticEvent = {
        target: {
          name,
          value: val,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    },
    [onChange, name]
  );

  return (
    <GdsDateInput
      ref={ref}
      input-id={`${inputId}${highlight ? "-with-error" : ""}`}
      name={name}
      date-format={dateFormat}
      min-date={minDate}
      max-date={maxDate}
      placeholder={placeholder}
      disabled={disabled}
      aria-label="Date. Please enter 2 digit month, 2 digit day, and 4 digit year"
      input-size={13}
      date-input-value-change={handleDateChange}
      date-input-date-selected={handleDateChange}
    >
      {`${labelText}`}
      {highlight && <span slot="error-message"></span>}
    </GdsDateInput>
  );
};

export default DatePicker;
